import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { jsonFetch } from '@/utils/jsonFetch'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface ProfileDescriptor {
  nickname?: string
  age?: string
  age_approx?: string
  gender_presentation?: string
  hair_color?: string
  hair_style?: string
  hair_length?: string
  skin_tone?: string
  eye_color?: string
  build?: string
  height?: string
  distinguishing_features?: string[]
  typical_clothing?: string
}

interface ProfileData {
  student: {
    id: string
    full_name: string | null
    display_name: string | null
    grade: string | null
  }
  profile: {
    photo_urls: string[]
    descriptor: ProfileDescriptor
    guardian_consent: boolean
    participant_id: string | null
  }
}

const HAIR_COLORS = ['Black', 'Dark brown', 'Light brown', 'Blonde', 'Red', 'Auburn', 'Gray', 'White']
const HAIR_STYLES = ['Straight', 'Wavy', 'Curly', 'Coily', 'Braided', 'In a ponytail', 'In two braids', 'Afro', 'Buzz cut']
const HAIR_LENGTHS = ['Very short', 'Short', 'Medium', 'Shoulder-length', 'Long', 'Very long']
const EYE_COLORS = ['Dark brown', 'Light brown', 'Hazel', 'Green', 'Blue', 'Gray']
const BUILDS = ['Petite', 'Average', 'Athletic', 'Tall for their age', 'Stocky']

function FieldSelect({ label, value, options, onChange }: {
  label: string; value: string; options: string[]; onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60 mb-1">{label}</label>
      <select
        className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select...</option>
        {options.map((opt) => (
          <option key={opt} value={opt.toLowerCase()}>{opt}</option>
        ))}
      </select>
    </div>
  )
}

function FieldText({ label, value, placeholder, onChange }: {
  label: string; value: string; placeholder?: string; onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60 mb-1">{label}</label>
      <input
        type="text"
        className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export default function StudentProfileBuilder() {
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [studentName, setStudentName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [grade, setGrade] = useState('')
  const [nickname, setNickname] = useState('')
  const [age, setAge] = useState('')
  const [genderPresentation, setGenderPresentation] = useState('')

  const [hairColor, setHairColor] = useState('')
  const [hairStyle, setHairStyle] = useState('')
  const [hairLength, setHairLength] = useState('')
  const [skinTone, setSkinTone] = useState('')
  const [eyeColor, setEyeColor] = useState('')
  const [build, setBuild] = useState('')
  const [height, setHeight] = useState('')
  const [typicalClothing, setTypicalClothing] = useState('')
  const [distinguishingFeatures, setDistinguishingFeatures] = useState('')
  const [traits, setTraits] = useState('')

  const loadProfile = useCallback(async () => {
    if (!studentId) return
    setLoading(true)
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/parent/students/${studentId}/profile`) as any
      const data = (raw?.data ?? raw) as ProfileData
      const s = data.student
      const p = data.profile?.descriptor || {}

      setStudentName(s.full_name || '')
      setDisplayName(s.display_name || s.full_name || '')
      setGrade(s.grade || '')
      setNickname(p.nickname || '')
      setAge(p.age || p.age_approx || '')
      setGenderPresentation(p.gender_presentation || '')
      setHairColor(p.hair_color || '')
      setHairStyle(p.hair_style || '')
      setHairLength(p.hair_length || '')
      setSkinTone(p.skin_tone || '')
      setEyeColor(p.eye_color || '')
      setBuild(p.build || '')
      setHeight(p.height || '')
      setTypicalClothing(p.typical_clothing || '')
      setDistinguishingFeatures((p.distinguishing_features || []).join(', '))
      setTraits('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [studentId])

  useEffect(() => { loadProfile() }, [loadProfile])

  const handleSave = async () => {
    if (!studentId) return
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      await jsonFetch(`${CORE_API}/api/v1/parent/students/${studentId}/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          display_name: displayName || undefined,
          grade: grade || undefined,
          descriptor: {
            nickname: nickname || undefined,
            age: age || undefined,
            age_approx: age ? `${age}-year-old` : undefined,
            gender_presentation: genderPresentation || undefined,
            hair_color: hairColor || undefined,
            hair_style: hairStyle || undefined,
            hair_length: hairLength || undefined,
            skin_tone: skinTone || undefined,
            eye_color: eyeColor || undefined,
            build: build || undefined,
            height: height || undefined,
            typical_clothing: typicalClothing || undefined,
            distinguishing_features: distinguishingFeatures
              ? distinguishingFeatures.split(',').map((s) => s.trim()).filter(Boolean)
              : undefined,
          },
          traits: traits ? traits.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        }),
      })
      setSuccess('Profile saved! This information helps create personalized storybook illustrations.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="p-6 text-cocoa/60">Loading profile...</div>

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <button
        type="button"
        className="text-sm text-cocoa/60 hover:text-cocoa"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div>
        <h2 className="text-xl font-bold text-cocoa">{studentName}'s Profile</h2>
        <p className="text-sm text-cocoa/60">
          Help us bring your child to life in their storybooks! These details create personalized illustrations.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-brick/40 bg-brick/10 px-4 py-2 text-sm text-brick">{error}</div>
      )}
      {success && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">{success}</div>
      )}

      {/* Basic Info */}
      <section className="rounded-2xl bg-white/85 p-5 shadow-soft space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Basic Information</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldText label="Display Name" value={displayName} onChange={setDisplayName} placeholder="What they go by" />
          <FieldText label="Nickname" value={nickname} onChange={setNickname} placeholder="Optional nickname" />
          <FieldText label="Age" value={age} onChange={setAge} placeholder="e.g. 8" />
          <FieldText label="Grade" value={grade} onChange={setGrade} placeholder="e.g. 3rd" />
          <FieldSelect label="Gender Presentation" value={genderPresentation} options={['Girl', 'Boy', 'Non-binary']} onChange={setGenderPresentation} />
        </div>
      </section>

      {/* Appearance */}
      <section className="rounded-2xl bg-white/85 p-5 shadow-soft space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Appearance</h3>
        <p className="text-xs text-cocoa/50">These details help the AI illustrate your child consistently across storybook pages.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldSelect label="Hair Color" value={hairColor} options={HAIR_COLORS} onChange={setHairColor} />
          <FieldSelect label="Hair Style" value={hairStyle} options={HAIR_STYLES} onChange={setHairStyle} />
          <FieldSelect label="Hair Length" value={hairLength} options={HAIR_LENGTHS} onChange={setHairLength} />
          <FieldText label="Skin Tone" value={skinTone} onChange={setSkinTone} placeholder="e.g. warm brown, fair with freckles" />
          <FieldSelect label="Eye Color" value={eyeColor} options={EYE_COLORS} onChange={setEyeColor} />
          <FieldSelect label="Build" value={build} options={BUILDS} onChange={setBuild} />
          <FieldText label="Height" value={height} onChange={setHeight} placeholder="e.g. tall for their age" />
          <FieldText label="Typical Clothing" value={typicalClothing} onChange={setTypicalClothing} placeholder="e.g. colorful t-shirts and jeans" />
        </div>
        <FieldText
          label="Distinguishing Features"
          value={distinguishingFeatures}
          onChange={setDistinguishingFeatures}
          placeholder="e.g. round glasses, dimples, gap-toothed smile (comma-separated)"
        />
      </section>

      {/* Personality */}
      <section className="rounded-2xl bg-white/85 p-5 shadow-soft space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Personality & Interests</h3>
        <FieldText
          label="Traits & Interests"
          value={traits}
          onChange={setTraits}
          placeholder="e.g. curious, loves dinosaurs, plays soccer (comma-separated)"
        />
      </section>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          className="rounded-xl border border-sand px-4 py-2 text-sm text-cocoa/70 hover:bg-sand/20"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-xl bg-brick px-6 py-2 text-sm font-semibold text-white hover:bg-brickDark disabled:opacity-50"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  )
}
